import React, { useEffect, useMemo, useState } from "react";
import { SelectPicker, Input, InputNumber } from "rsuite";
import { Schema } from "rsuite";
import styled from "styled-components";

import { renderMoney, renderColumn } from "utils";
import TableEditCellsNew from "components/Table/TableEditCells/TableEditCellsNew";

/**
 * Props expected (kept from legacy component):
 * - data: Array of row objects
 * - loading: boolean
 * - service: boolean
 * - filter: { str?: string, antf_key?: number }
 * - numberTypeFamilyList, worldzoneList, destinationList, subdestinationList: reference lists
 * - accountId: string|number
 * - onEditItem: (accountId, alf_key, payload) => Promise<any>
 * - onRemoveItem: (rowData) => void
 * - update: () => void   // refresh list after save
 */

const { StringType, NumberType } = Schema.Types;

const AccessListFiltersTable = ({
    data = [],
    loading,
    service,
    formModel,
    filter,
    numberTypeFamilyList = [],
    worldzoneList = [],
    destinationList = [],
    subdestinationList = [],
    serviceCreateValue = null,
    setServiceCreateValue,

    ...props
}) => {
    // Local state
    const [tableData, setTableData] = useState([]);

    // --- Filtering logic (ported) ---
    const searchedData = useMemo(
        () => [
            "a_worldzone_name",
            "a_destination_name",
            "a_subdestination_name",
            "a_prefix",
            "b_worldzone_name",
            "b_destination_name",
            "b_subdestination_name"
        ],
        []
    );

    useEffect(() => {
        if (!data) return;

        const strMatch = filter?.str?.toLowerCase();
        const antfMatch = filter?.antf_key;

        const next = data.filter((item) => {
            // String part
            const strMatchedList = searchedData.map((keyName) => {
                if (!strMatch) return true;
                const raw = item[keyName];
                if (!raw) return false;
                const matchedString = String(raw).toLowerCase();

                if (keyName === "a_prefix") {
                    // prefix: startsWith both ways (short/long)
                    if (matchedString.length > strMatch.length) {
                        return matchedString.indexOf(strMatch) === 0;
                    }

                    return strMatch.indexOf(matchedString) === 0;
                }

                return strMatch.includes(matchedString);
            });

            // ANT family
            const antfMatchedValue = !antfMatch || item.antf_key === antfMatch;

            return strMatchedList.some(Boolean) && antfMatchedValue;
        });

        setTableData(next);
    }, [data, filter, searchedData]);

    // Helpers to read/save row form values coming from TableEditCellsNew

    // --- Columns ---
    const columns = useMemo(() => {
        return [
            ...(service ? [{
                id: "antf_key",
                name: "Service name",
                dataKey: "antf_key",
                align: "center",
                minWidth: 130,
                flexGrow: 1,
                className: "tableFilters_column",
                accepter: SelectPicker,
                placeholder: "Service name",
                data: numberTypeFamilyList,
                valueKey: "antf_key",
                labelKey: "family_name",
                value: ({ antf_name }) => antf_name || "",
                defaultValue: ({ antf_key }) => antf_key,
                extraCreateProps: {
                    onChange: (value) => {
                        setServiceCreateValue(value);
                    }
                }
            },
            {
                id: "a_wz_key",
                name: "Origin zone",
                dataKey: "a_wz_key",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                className: "tableFilters_column",
                accepter: SelectPicker,
                placeholder: "Origin zone",
                data: worldzoneList,
                valueKey: "wz_key",
                labelKey: "name",
                value: ({ a_worldzone_name }) => a_worldzone_name || "",
                defaultValue: ({ a_wz_key }) => a_wz_key,
                extraCreateProps: {
                    disabled: !!serviceCreateValue
                }
            },
            {
                id: "a_de_key",
                name: "Origin country",
                dataKey: "a_de_key",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                accepter: SelectPicker,
                placeholder: "Origin country",
                data: destinationList,
                valueKey: "de_key",
                labelKey: "name",
                value: ({ a_destination_name }) => a_destination_name || "",
                defaultValue: ({ a_de_key }) => a_de_key,
                extraCreateProps: {
                    disabled: !!serviceCreateValue
                }
            },
            {
                id: "a_sde_key",
                name: "Origin operator",
                dataKey: "a_sde_key",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                accepter: SelectPicker,
                placeholder: "Origin operator",
                data: subdestinationList,
                valueKey: "sde_key",
                labelKey: "name",
                value: ({ a_subdestination_name }) => a_subdestination_name || "",
                defaultValue: ({ a_sde_key }) => a_sde_key,
                extraCreateProps: {
                    disabled: !!serviceCreateValue
                }
            }] : []),
            {
                id: "a_prefix",
                name: service ? "Origin Prefix" : "Service ID",
                dataKey: "a_prefix",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                accepter: Input,
                placeholder: service ? "Origin Prefix" : "Service ID",
                value: ({ a_prefix }) => a_prefix ?? "",
                defaultValue: ({ a_prefix }) => a_prefix,
                extraCreateProps: {
                    disabled: !!serviceCreateValue
                }
            },
            {
                id: "b_wz_key",
                name: "Desctination zone",
                dataKey: "b_wz_key",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                accepter: SelectPicker,
                placeholder: "Desctination zone",
                data: worldzoneList,
                valueKey: "wz_key",
                labelKey: "name",
                value: ({ b_worldzone_name }) => b_worldzone_name || "",
                defaultValue: ({ b_wz_key }) => b_wz_key
            },

            {
                id: "b_de_key",
                name: "Destination country",
                dataKey: "b_de_key",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                accepter: SelectPicker,
                placeholder: "Destination country",
                data: destinationList,
                valueKey: "de_key",
                labelKey: "name",
                value: ({ b_destination_name }) => b_destination_name || "",
                defaultValue: ({ b_de_key }) => b_de_key
            },
            {
                id: "b_sde_key",
                name: "Destination operator",
                dataKey: "b_sde_key",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                accepter: SelectPicker,
                placeholder: "Destination operator",
                data: subdestinationList,
                valueKey: "sde_key",
                labelKey: "name",
                value: ({ b_subdestination_name }) => b_subdestination_name || "",
                defaultValue: ({ b_sde_key }) => b_sde_key
            },
            {
                id: "rate_min",
                name: "Minimal payout",
                dataKey: "rate_min",
                align: "center",
                flexGrow: 1,
                minWidth: 130,
                accepter: InputNumber,
                placeholder: "Minimal payout",
                step: 0.1,
                min: 0,
                value: ({ rate_min }) => renderMoney(rate_min),
                defaultValue: ({ rate_min }) => rate_min
            }
        ];
    }, [
        service,
        numberTypeFamilyList,
        worldzoneList,
        destinationList,
        subdestinationList,
        serviceCreateValue
    ]);

    const renderedColumns = useMemo(() => columns.map(renderColumn), [columns]);

    // Height calculation (ported)
    const calculatedTableHeight = typeof window !== "undefined" ? window.innerHeight - 515 : 662;
    const calculatedTableRows = Math.round(calculatedTableHeight / 45);
    const defaultTableRowsHeight = 5 * 45 + 47;

    const defaultCreateFormValue = useMemo(() => service ? {
        a_prefix: "",
        a_de_key: null,
        a_sde_key: null,
        a_wz_key: null,
        antf_key: null,
        b_de_key: null,
        b_sde_key: null,
        b_wz_key: null,
        rate_min: null
    } : {
        a_prefix: "",
        b_wz_key: null,
        b_de_key: null,
        b_sde_key: null,
        rate_min: null
    }, [service]);

    return (
        <Wrapper>
            <StyledTable
                rowKey="alf_key"
                rowIdKey="alf_key"
                className="tableFilters"
                columns={columns}
                data={tableData}
                loading={loading}
                headerHeight={47}
                rowHeight={45}
                addButtonWidth={120}
                height={calculatedTableHeight < defaultTableRowsHeight ? defaultTableRowsHeight : calculatedTableHeight}
                autoHeight={tableData.length <= calculatedTableRows}
                formModel={formModel}
                errorPlacement="topEnd"
                createFormDefaultValue={defaultCreateFormValue}
                {...props}
            // This callback is emitted by TableEditCellsNew for per-row form updates (assumed API)
            >
                {renderedColumns}
            </StyledTable>
        </Wrapper>
    );
};

export default AccessListFiltersTable;

const Wrapper = styled.div`
    .tableFilters__previewText {
        display: block;
        line-height: 20px;
        margin-top: 7px;
        word-break: normal;
    }

    .tableFilters_buttons {
        align-items: center;
        display: flex;
        height: 20px;
        margin-top: 7px;
    }

    .tableFilters_buttons .rs-btn-sm {
        min-width: 28px;
        min-height: 28px;
        padding: 0px;
        margin-right: 8px;
    }
`;

const StyledTable = styled(TableEditCellsNew)`
`;